import { ReactNode, createElement } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";

import { UNSAFE_media } from "@atlaskit/primitives/responsive";

import {
    ExportableAdgColorTypes,
    IconNameTypes,
    Icon,
    UNSAFE_container
} from "../../../../libraries/wpl/foundations/src/index";
import { Button, ButtonProps } from "../../button/src/";
import {
    Image,
    AspectRatios,
    PictureProps,
    Picture
} from "../../image/src/index";
import { Link, LinkProps } from "../../link/src/index";

export type HeaderTagTypes = "h1" | "h2" | "h3" | "h4" | "h5" | undefined;
export type OrientationTypes = "horizontal" | "vertical";

export interface ButtonAndLinkProps {
    buttonProps: ButtonProps;
    primaryLinkProps: LinkProps;
}

export interface ButtonAndSecondaryLinkProps {
    buttonProps: ButtonProps;
    secondaryLinkProps: LinkProps;
}

export interface OneLinkProps {
    primaryLinkProps?: LinkProps;
}

export interface OneButtonProps {
    buttonProps: ButtonProps;
}

export interface TwoLinksProps {
    primaryLinkProps: LinkProps;
    secondaryLinkProps: LinkProps;
}

export interface EyebrowProps {
    eyebrowText: string;
}

export type CTAProps =
    | OneButtonProps
    | OneLinkProps
    | ButtonAndLinkProps
    | ButtonAndSecondaryLinkProps
    | TwoLinksProps;

export interface ContentBlockProps {
    headerText: string | ReactNode;
    bodyText?: string | ReactNode;
    headerType?: HeaderTagTypes;
    footerHeader?: string;
    footerText?: string;
    /**
     * @deprecated - Please use pictureProps instead
     */
    imageLink?: string;
    /**
     * @deprecated - Please use pictureProps instead
     */
    imageAltText?: string;
    pictureProps?: PictureProps;
    decoration?: string;
    eyebrowText?: string;
    iconName?: IconNameTypes;
    iconColor?: ExportableAdgColorTypes;
    className?: string;
    ctaProps?: CTAProps;
    isCentered?: boolean;
    cardStyle?: boolean;
    /**
     * @deprecated - Please use pictureProps instead
     */
    aspectRatio?: AspectRatios;
    orientation?: OrientationTypes;
    horizontalRule?: boolean;
}

function getHeaderTag(text: string | ReactNode, header: HeaderTagTypes) {
    const headerTag = header ? header : "h3";
    return createElement(headerTag, null, text);
}

/* Gives h tag one less than header */
function getEyebrowTag(
    headerType: HeaderTagTypes | undefined,
    eyebrowText: string | undefined
) {
    const tagNumber = headerType ? Number(headerType.slice(-1)) : 5;
    return createElement(
        `h${tagNumber + 1}`,
        { className: "contentBlock-eyebrow" },
        eyebrowText
    );
}

function renderLinks(ctaProps?: CTAProps) {
    if (!ctaProps) {
        return null;
    }
    let firstLink;
    if ("buttonProps" in ctaProps) {
        firstLink = <Button {...ctaProps.buttonProps} />;
    } else if ("primaryLinkProps" in ctaProps && ctaProps.primaryLinkProps) {
        firstLink = <Link {...ctaProps.primaryLinkProps} />;
    }
    const secondaryLink =
        "secondaryLinkProps" in ctaProps ? (
            <Link {...ctaProps.secondaryLinkProps} />
        ) : null;
    return (
        <div className="contentBlock-linkFlex">
            {firstLink}
            {secondaryLink}
        </div>
    );
}

/**
 * This method retrieves either the eyebrow or icon decoration. If both are present, the icon is returned.
 * @param headerType
 */
const getDecoration = (
    headerType?: HeaderTagTypes,
    eyebrowText?: string,
    iconName?: IconNameTypes,
    iconColor?: ExportableAdgColorTypes
) => {
    if (iconName) {
        return <Icon name={iconName} color={iconColor} />;
    } else if (eyebrowText) {
        return getEyebrowTag(headerType, eyebrowText);
    }
    return null;
};

const getImage = (
    pictureProps?: PictureProps,
    imageLink?: string,
    imageAltText?: string,
    aspectRatio?: AspectRatios
) => {
    if (pictureProps) {
        return (
            <div className="img-section">
                <Picture {...pictureProps} />
            </div>
        );
    } else if (imageLink && imageAltText) {
        return (
            <div className="img-section">
                <Image
                    src={imageLink}
                    alt={imageAltText}
                    aspectRatio={aspectRatio}
                />
            </div>
        );
    } else {
        return null;
    }
};

const containerStyle = (
    isCentered: boolean,
    direction?: string,
    cardStyle?: boolean,
    headerType?: string
) => css`
    ${direction == "horizontal"
        ? `
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    gap: 1rem;
    .img-section {
      img {
        height: 100%;
      }
    }
    ${UNSAFE_container.below.xs} {
      grid-template-columns: minmax(0, 1fr);
      gap: 0;
    }

  `
        : `
    display: flex;
    flex-direction: column;
  `};

    .img-section {
        line-height: 0;
        display: block;

        img {
            width: 100%;
        }

        & + .text-section {
            ${cardStyle ? "" : "margin-top: 0.5rem;"}
        }
    }

    .text-section {
        ${cardStyle ? "padding: 20px;" : ""}
        text-align: ${isCentered ? "center" : "left"};
        display: flex;
        flex-direction: column;

        & > * {
            margin: 0 0 24px;
        }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-weight: 500;
        line-height: 1.16;
    }

    h1 {
        font-size: 3rem;

        ${UNSAFE_media.only.sm} {
            font-size: 2.75rem;
        }

        ${UNSAFE_media.below.sm} {
            font-size: 2.25rem;
        }
    }

    h2 {
        font-size: 2.75rem;

        ${UNSAFE_media.only.sm} {
            font-size: 2.5rem;
        }

        ${UNSAFE_media.below.sm} {
            font-size: 2.25rem;
        }
    }

    h3 {
        font-size: 1.5rem;
    }

    h4 {
        font-size: 1.25rem;
    }

    p {
        line-height: 1.4;
        ${headerType == "h1"
            ? `
    font-size: 1.25rem;
    letter-spacing: 0.018rem;
    font-family: var(--bxp-charlie-display-font-family);
    `
            : ``};
    }

    a,
    button {
        margin-top: 0.5rem;
        width: fit-content;
        font-weight: 500;
    }

    .contentBlock-body {
        font-weight: 400;
    }

    .contentBlock-eyebrow {
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.1rem;
        text-transform: uppercase;
        order: -1;
    }

    .icon {
        order: -1;
    }

    .contentBlock-linkFlex {
        display: flex;
        flex-wrap: wrap;
        gap: 10%;
        justify-content: ${isCentered ? "center" : "flex-start"};
        align-items: center;
    }
`;

export const ContentBlock = ({
    headerText,
    bodyText,
    headerType = "h3",
    eyebrowText,
    className = "",
    imageLink,
    imageAltText,
    pictureProps,
    iconName,
    iconColor,
    ctaProps,
    aspectRatio = "1/1",
    orientation,
    cardStyle,
    isCentered = false,
    horizontalRule
}: ContentBlockProps) => {
    if (imageLink && !imageAltText) {
        throw new Error(
            "If using an image, please provide an alt text for accessibility purposes!"
        );
    }
    return (
        <div
            className={`content-block ${className}`}
            css={containerStyle(isCentered, orientation, cardStyle, headerType)}
        >
            {/* Header is first in HTML for accessibility but order is changed visually through CSS */}
            {getImage(pictureProps, imageLink, imageAltText, aspectRatio)}

            <div className="text-section">
                {headerText && getHeaderTag(headerText, headerType)}

                {getDecoration(headerType, eyebrowText, iconName, iconColor)}

                {horizontalRule && (
                    <hr
                        css={css({
                            height: "2px",
                            maxWidth: "1200px"
                        })}
                    />
                )}

                {bodyText && <p className="contentBlock-body">{bodyText}</p>}

                {renderLinks(ctaProps)}
            </div>
        </div>
    );
};
